import type { RefMeta } from './references-context';

type Listener = () => void;

/**
 * Framework-agnostic keyed pub/sub store for `RefMeta` rows, keyed by
 * reference token id. Backs `useReferenceMeta`/`useReferenceMetas` — unlike
 * the plain `ReferencesProvider` context (which broadcasts its whole `Map`
 * to every consumer on every update), this only notifies listeners
 * subscribed to a token id whose row actually changed value.
 */
export class ReferenceMetaStore {
  private map: Map<string, RefMeta> = new Map();
  private readonly listenersByTokenId = new Map<string, Set<Listener>>();

  getSnapshot = (tokenId: string): RefMeta | undefined => {
    return this.map.get(tokenId);
  };

  getAll = (): Map<string, RefMeta> => this.map;

  subscribe = (tokenId: string, listener: Listener): (() => void) => {
    let listeners = this.listenersByTokenId.get(tokenId);
    if (!listeners) {
      listeners = new Set();
      this.listenersByTokenId.set(tokenId, listeners);
    }
    listeners.add(listener);
    return () => {
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) {
        this.listenersByTokenId.delete(tokenId);
      }
    };
  };

  /**
   * Replaces the whole map (e.g. a fresh `references` query result) and
   * notifies only the token ids whose row reference actually changed —
   * added, removed, or replaced with a `!==` value. Rows that are
   * reference-identical across updates (unrelated tokens) notify no one.
   */
  setAll = (next: Map<string, RefMeta>): void => {
    const previous = this.map;
    if (previous === next) return;
    this.map = next;

    const changedTokenIds = new Set<string>();
    for (const [tokenId, value] of next) {
      if (previous.get(tokenId) !== value) changedTokenIds.add(tokenId);
    }
    for (const tokenId of previous.keys()) {
      if (!next.has(tokenId)) changedTokenIds.add(tokenId);
    }

    for (const tokenId of changedTokenIds) {
      const listeners = this.listenersByTokenId.get(tokenId);
      if (!listeners) continue;
      // Snapshot before iterating — a listener may synchronously unsubscribe.
      for (const listener of [...listeners]) listener();
    }
  };
}
